import { useEffect, useMemo, useRef, useState } from 'react';
import type { Message } from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import { useChatStore } from '../chat/chatStore.js';
import { useDirectory } from '../chat/directoryStore.js';
import { counterpartId, memberLabel } from '../chat/labels.js';
import { useSocket } from '../socket/SocketProvider.js';
import { Avatar } from './Avatar.js';
import { Icon } from './Icon.js';

const ACK_TIMEOUT_MS = 10_000;

/**
 * "Forward to…": pick one or more of your conversations. The copies credit the
 * original author, and a forwarded file is the same held file, not a new one.
 */
export function ForwardDialog({
  message,
  onDone,
}: {
  message: Message;
  /** Called with the number of conversations it went to, or 0 when cancelled. */
  onDone: (count: number) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { socket } = useSocket();
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const channels = useChatStore((state) => state.channels);
  const memberIds = useMemo(() => channels.flatMap((channel) => channel.memberIds), [channels]);
  const directory = useDirectory(memberIds);

  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.showModal();
    // After `showModal`, which focuses the first focusable child itself.
    searchRef.current?.focus();
  }, []);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return channels
      .map((channel) => {
        const other = channel.type === 'direct' ? counterpartId(channel, currentUserId) : null;
        const title = other ? memberLabel(other, currentUserId, directory) : channel.name;
        return { channel, other, title };
      })
      .filter((row) => !needle || row.title.toLowerCase().includes(needle));
  }, [channels, currentUserId, directory, query]);

  function toggle(channelId: string): void {
    setPicked((current) =>
      current.includes(channelId)
        ? current.filter((id) => id !== channelId)
        : [...current, channelId],
    );
  }

  async function send(): Promise<void> {
    if (!socket || picked.length === 0) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const result = await socket.timeout(ACK_TIMEOUT_MS).emitWithAck('chat.forward', {
        channelId: message.channelId,
        messageId: message.id,
        targetChannelIds: picked,
      });

      if (result.ok) {
        onDone(picked.length);
      } else {
        setError(result.message);
      }
    } catch {
      setError('No answer from the server. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} className="modal" onClose={() => onDone(0)} aria-labelledby="forward-title">
      <header className="modal-head">
        <div>
          <h2 id="forward-title">Forward message</h2>
          <p className="truncate">{message.content}</p>
        </div>
        <button
          type="button"
          className="btn btn--icon"
          onClick={() => onDone(0)}
          aria-label="Close"
        >
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="modal-scroll">
        <div className="input-group">
          <Icon name="search" size={15} />
          <input
            className="input"
            placeholder="Search conversations"
            ref={searchRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <ul className="result-list">
          {rows.map(({ channel, other, title }) => {
            const chosen = picked.includes(channel.id);

            return (
              <li key={channel.id}>
                <button
                  type="button"
                  className={chosen ? 'result result--picked' : 'result'}
                  aria-pressed={chosen}
                  onClick={() => toggle(channel.id)}
                >
                  <Avatar name={title} id={other ?? channel.id} group={!other} size="sm" />
                  <span className="result-body">
                    <span className="result-name truncate" dir="auto">
                      {title}
                    </span>
                  </span>
                  <span className="result-action">
                    <Icon name={chosen ? 'check' : 'plus'} size={14} />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {error ? (
          <p className="field-error" role="alert">
            <Icon name="alertCircle" size={13} />
            {error}
          </p>
        ) : null}
      </div>

      <footer className="modal-foot">
        <span className="field-hint">
          {picked.length === 0 ? 'Choose where to send it' : `${picked.length} selected`}
        </span>
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || picked.length === 0}
          onClick={() => void send()}
        >
          {busy ? <span className="btn-spinner" /> : <Icon name="forward" size={14} />}
          Forward
        </button>
      </footer>
    </dialog>
  );
}
